/**
 * @description 类型检测
 */
// 操作系统类型
export type OsType = 'linux' | 'windows' | 'mac os' | 'android' | 'ios' | 'unknown' | string;
export enum OS_TYPE {
  linux = 'linux',
  windows = 'windows',
  macOs = 'mac os',
  android = 'android',
  ios = 'ios',
  unknown = 'unknown'
}

// 浏览器类型
export type BrowserType = 'chrome' | 'safari' | 'tbs' | 'unknown' | string;
export enum BROWSER_TYPE {
  chrome = 'chrome',
  safari = 'safari',
  tbs = 'tbs',
  unknown = 'unknown'
}

// App类型
export type AppType = 'tenvideo' | 'weixin' | 'qq' | 'qqbrowser' | 'weibo' | 'unknown' | string;
export enum APP_TYPE {
  tenvideo = 'tenvideo',
  weixin = 'weixin',
  qq = 'qq',
  qqbrowser = 'qqbrowser',
  weibo = 'weibo',
  unknown = 'unknown'
}

// 设备类型
export type DeviceType =
  | 'iphone'
  | 'ipad'
  | 'huawei'
  | 'xiaomi'
  | 'oppo'
  | 'vivo'
  | 'samsung' // 三星
  | 'oneplus' // 一加
  | 'skr' // 黑鲨
  | 'nubia' // 努比亚
  | 'smartisan' // 锤子手机
  | 'realme' // realme
  | 'unknown'
  | string;
export enum DEVICE_TYPE {
  iphone = 'iphone',
  ipad = 'ipad',
  huawei = 'huawei',
  xiaomi = 'xiaomi',
  oppo = 'oppo',
  vivo = 'vivo',
  samsung = 'samsung',
  oneplus = 'oneplus',
  skr = 'skr',
  nubia = 'nubia',
  smartisan = 'smartisan',
  realme = 'realme',
  unknown = 'unknown'
}

// 属性类型
type AttributeType = 'osType' | 'browserType' | 'appType' | 'deviceType';
// 属性版本
type AttributeVersion = 'osVersion' | 'browserVersion' | 'appVersion' | 'deviceVersion';

class DetectedInfo {
  public osType: OsType;
  public osVersion: string;
  public browserType: BrowserType;
  public browserVersion: string;
  public appType: AppType;
  public appVersion: string;
  public deviceType: DeviceType;
  public deviceVersion: string;
  public ua: string;
  public isMobile: boolean;
  public constructor(ua?: string) {
    this.osType = 'unknown';
    this.osVersion = '0.0.0';
    this.browserType = 'unknown';
    this.browserVersion = '0.0.0';
    this.appType = 'unknown';
    this.appVersion = '0.0.0';
    this.deviceType = 'unknown';
    this.deviceVersion = '0.0.0';
    this.ua = ua || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    this.isMobile = /mobile/i.test(this.ua);
  }
}

type OsRule = [string, OsType, RegExp];
type BrowserRule = [string, BrowserType, RegExp];
type AppRule = [string, AppType, RegExp];
type DeviceRule = [string, DeviceType, RegExp];

// 操作系统正则规则
const osRules: OsRule[] = [
  ['os', 'ios', /(iPhone|iPad) OS?\s+(\w+)/i],
  ['os', 'android', /(Android)\s+([\d+.]+);/i],
  ['os', 'linux', /(Linux)\s+\w+;\s+rv:([\d.]+)/i],
  ['os', 'windows', /(Windows)\sNT?\s([\d.]+)/i],
  ['os', 'mac os', /(Mac\s?OS\s?X?)\s+(\w+)\)/],
];

// 浏览器正则规则
const browserRules: BrowserRule[] = [
  ['browser', 'chrome', /(Chrome)\/([\d.]+)/i],
  ['browser', 'safari', /Version\/([\d.]+)\s+(Safari)\/[\d.]+/i], // 需要将version和name对调位置
  ['browser', 'applewebkit', /(AppleWebKit)\/([\d.]+)/],
  ['browser', 'tbs', /([\d.]+)\s+(TBS)/i], // 需要将version和name对调位置
];

// App正则规则
const appRules: AppRule[] = [
  ['app', 'tenvideo', /(QQLiveHDBrowser|QQLiveBrowser|qqlive|QQLive_MAC)\/([\d.]+)/i],
  ['app', 'qq', /\s+(QQ)\/([\d.]+)/i],
  ['app', 'weixin', /(MicroMessenger)\/([\d.]+)/i],
  ['app', 'weibo', /(weibo)_*([\d.]+)/i],
  ['app', 'qqbrowser', /\s+(MQQBrowser)\/([\d.]+)/i],
];

// 设备正则规则
const deviceRules: DeviceRule[] = [
  ['device', 'mac', /\((Macintosh);.*?([\d_]+)\s?\)/i],
  ['device', 'windows', /(Windows)\sNT?\s([\d.]+)/i],
  ['device', 'ipad', /(iPad)/i],
  ['device', 'iphone', /(iPhone)/i],
  ['device', 'skr', /(SKR)-?(\w+)/i],
  ['device', 'huawei', /(HUAWEI)/i],
  ['device', 'nubia', /(nubia)/i],
  ['device', 'oneplus', /(oneplus)/i],
  ['device', 'oppo', /(oppo)/i],
  ['device', 'realme', /(realme)/i],
  ['device', 'samsung', /(samsung)/i],
  ['device', 'smartisan', /(smartisan)/i],
  ['device', 'vivo', /(vivo)/i],
  ['device', 'xiaomi', /(XiaoMi)/i],
  ['device', 'android', /Android/i],
];

const rules = [appRules, osRules, browserRules, deviceRules];

// tbs和safari特殊逻辑处理
const fixTbsAndSafari = (
  prefix: AttributeType,
  ruleType: BrowserType,
  matchType: string,
  matchVersion: string,
) => {
  if (prefix === 'browserType' && /safari|tbs/.test(ruleType)) {
    return [matchVersion, matchType];
  }
  return [matchType, matchVersion];
};

// 腾讯视频windows客户端特殊处理
const fixTenvideoWindowsApp = (prefix: AttributeType, ruleType: AppType, matchVersion: string) => {
  if (prefix === 'appType' && ruleType === 'tenvideo' && matchVersion) {
    return matchVersion.replace(/(\d{2})(\d{2})(\d{4})/, '$1.$2.$3');
  }
  return matchVersion;
};

export function detect(userAgent?: string): DetectedInfo {
  const detectInfo = new DetectedInfo(userAgent);
  const ua = userAgent || detectInfo.ua;
  if (!ua) {
    return detectInfo;
  }
  rules.forEach((rule) => {
    const len = rule.length;
    for (let i = 0; i < len; i += 1) {
      const r = rule[i];
      const [prefix, ruleType, reg] = r;
      const match = reg.exec(ua);
      if (match) {
        match.shift();
        let [matchType, matchVersion] = match;
        const type = `${prefix}Type` as AttributeType;
        const version = `${prefix}Version` as AttributeVersion;
        [matchType, matchVersion] = fixTbsAndSafari(type, ruleType, matchType, matchVersion);
        matchVersion = fixTenvideoWindowsApp(type, ruleType, matchVersion);
        detectInfo[type] = matchType ? ruleType : 'unknown';
        detectInfo[version] = (matchVersion || '0.0.0').replace(/_/g, '.');
        break;
      }
    }
  });
  return detectInfo;
}
